import { dispatchError, useSetError } from "./modals";
import { flattenRoutes, LOCATIONS } from "../NavBar/locations";
import { KpiInfo } from "@blisspointmedia/bpm-types/dist/Kpis";
import { LagOption } from "@blisspointmedia/bpm-types/dist/StreamingPerformance";
import {
  CompanyFiscalCal,
  FiscalCalType,
  genPatternBasedCal,
} from "@blisspointmedia/bpm-types/dist/FiscalCalendar";
import { makeReducer } from "./utils";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useMemo, useState } from "react";
import * as R from "ramda";
import useLocation from "../utils/hooks/useLocation";
import {
  UsersLambdaFetch,
  awaitJSON,
  ToolsLambdaFetch,
  YoutubePerformanceLambdaFetch,
} from "../utils/fetch-utils";
import { keysToCamel } from "@blisspointmedia/bpm-types/dist/StyleUtils";
import { TEST_COMPANIES } from "@blisspointmedia/bpm-types/dist/TestCompanies";
import { YOUTUBE } from "@blisspointmedia/bpm-types/dist/MetricsTable";

const ROOT = "company";

export interface MobiusViewInfo {
  company: string;
  key: string;
  name: string;
  url: string;
}

export interface CustomDashboardInfo {
  name: string;
  url: string;
}

export interface CompanyInfo {
  agency: string;
  all_mobius_views?: MobiusViewInfo[];
  beeswax_advertiser_domain?: string;
  beeswax_default_click?: string;
  beeswax_iab_category?: string;
  cid: string;
  color?: string;
  commission?: number;
  competitors?: string[];
  defaultCrossChannelSource?: string; // TODO: Temporary until we decide where we will handle this configuration
  default_competitor?: string;
  disable_pages?: string[];
  fiscal_cal: CompanyFiscalCal;
  groups: string[];
  initial_kpi: string;
  kpi_filter_order?: string[];
  kpis: KpiInfo[];
  media_types?: string[];
  min_impressions_dates?: string;
  mobiusx_account_id?: string;
  amc_instance_id?: string;
  name_abbrev?: string;
  name: string;
  optimization_cid?: string;
  page_access_settings?: Record<string, string>;
  parent_company?: string;
  spike_kpis?: string[];
  streaming_performance_default_kpi: string;
  streaming_performance_default_lag: LagOption;
  sub_companies?: { name: string; cid: string }[];
  cross_channel_kpis: string[];
  custom_dashboards: CustomDashboardInfo[];
  salesforce_ids: string[];
  pacing_client_facing?: boolean;
  tinuiti_vertical_category: string | null;
  currency_code: string;
}

const DUMMY_COMPANY_INFO: CompanyInfo = {
  agency: "",
  cid: "",
  groups: [],
  initial_kpi: "",
  kpi_filter_order: [],
  kpis: [],
  name: "",
  streaming_performance_default_kpi: "",
  streaming_performance_default_lag: "1d",
  cross_channel_kpis: [],
  custom_dashboards: [],
  fiscal_cal: {},
  salesforce_ids: [],
  tinuiti_vertical_category: null,
  currency_code: "USD",
};

interface CompanyReduxState {
  companies: Record<string, CompanyInfo | undefined>;
  current: string;
  filteredKpis: Record<string, "fetching" | Record<string, boolean>>;
}

interface CompanyReduxRootState {
  [ROOT]: CompanyReduxState;
}

const SET_CURRENT_COMPANY = `${ROOT}/SET_CURRENT_COMPANY`;

interface SetCurrentCompanyActionType {
  type: string;
  company: string;
}

export const SetCompany = (company: string): SetCurrentCompanyActionType => ({
  type: SET_CURRENT_COMPANY,
  company,
});

const SET_COMPANY_INFO = `${ROOT}/SET_COMPANY_INFO`;
interface SetCompanyInfoActionType {
  type: string;
  company: string;
  info: Omit<CompanyInfo, "cid">;
}

export const SetCompanyInfo = (company: string, info: CompanyInfo): SetCompanyInfoActionType => ({
  type: SET_COMPANY_INFO,
  company,
  info,
});

const SET_FILTERED_KPIS = `${ROOT}/SET_FILTERED_KPIS`;
interface SetFilteredKpisActionType {
  type: string;
  company: string;
  filteredKpis: "fetching" | Record<string, boolean>;
}

const SET_FISCAL_CAL = `${ROOT}/SET_FISCAL_CAL`;

interface SetFiscalCalActionType {
  type: string;
  company: string;
  fiscalCal: CompanyFiscalCal;
}

export const SetFiscalCal = (
  company: string,
  fiscalCal: CompanyFiscalCal
): SetFiscalCalActionType => ({
  type: SET_FISCAL_CAL,
  company,
  fiscalCal,
});

export const fetchStreamingKpis = (
  company: string,
  prefix?: string,
  useTestParams?: boolean
): ((
  dispatch: ReturnType<typeof useDispatch>,
  getState: () => CompanyReduxRootState
) => Promise<void>) => async (dispatch, getState) => {
  const testParams = useTestParams ? { channel: prefix, pageID: prefix } : {};
  if (!filteredKpisSelector(getState())) {
    dispatch({
      type: SET_FILTERED_KPIS,
      company,
      filteredKpis: "fetching",
    });
    try {
      const validKpis = await ToolsLambdaFetch("/geo_kpis", {
        params: { company, ...testParams },
      });
      const res = await awaitJSON(validKpis);
      dispatch({
        type: SET_FILTERED_KPIS,
        company,
        filteredKpis: res,
      });
    } catch (e) {
      const reportError = e as Error;
      dispatchError(dispatch, {
        message: `Failed to fetch streaming kpi info. Error: ${reportError.message}`,
        reportError,
      });
    }
  }
};

export const fetchYoutubeKpis = (
  company: string,
  useTestParams?: boolean
): ((
  dispatch: ReturnType<typeof useDispatch>,
  getState: () => CompanyReduxRootState
) => Promise<void>) => async (dispatch, getState) => {
  const testParams = useTestParams ? { channel: YOUTUBE, pageID: YOUTUBE } : {};
  if (!filteredKpisSelector(getState())) {
    dispatch({
      type: SET_FILTERED_KPIS,
      company,
      filteredKpis: "fetching",
    });
    try {
      const validKpis = await YoutubePerformanceLambdaFetch("/kpis", {
        params: { company, ...testParams },
      });
      const res = await awaitJSON(validKpis);
      dispatch({
        type: SET_FILTERED_KPIS,
        company,
        filteredKpis: res,
      });
    } catch (e) {
      const reportError = e as Error;
      dispatchError(dispatch, {
        message: `Failed to fetch youtube kpi info. Error: ${reportError.message}`,
        reportError,
      });
    }
  }
};

export const currentCompanySelector = (state: CompanyReduxRootState): string => state[ROOT].current;
export const companyInfoSelector = (company: string) => (
  state: CompanyReduxRootState
): CompanyInfo | undefined => state[ROOT].companies[company];

export const mediaTypesSelector = (company: string) => (state: CompanyReduxRootState): string[] =>
  state[ROOT].companies[company]?.media_types || [];
export const initialKpiSelector = (company: string) => (
  state: CompanyReduxRootState
): string | undefined => state[ROOT].companies[company]?.initial_kpi;

export const competitorsSelector = (company: string) => (state: CompanyReduxRootState): string[] =>
  state[ROOT].companies[company]?.competitors || [];

export const disablePagesSelector = (company: string) => (state: CompanyReduxRootState): string[] =>
  state[ROOT].companies[company]?.disable_pages || [];

export const filteredKpisSelector = (
  state: CompanyReduxRootState
): "fetching" | Record<string, boolean> => state[ROOT].filteredKpis[currentCompanySelector(state)];

export const useCompanyInfo: (inputCompany?: string) => CompanyInfo = (inputCompany?: string) => {
  const locationCompany = useSelector(currentCompanySelector);
  const company = R.defaultTo(locationCompany, inputCompany);
  const companyInfo = useSelector(companyInfoSelector(company || ""));
  return companyInfo || DUMMY_COMPANY_INFO;
};

// Module-level cache for `/company`. It's fetched from several independent components
// (useCompanyInfoFetch, NavBar, NotFound), each with its own local cache, so a per-component guard
// can't dedup across them — and they fire at staggered times, not just concurrently. This singleton
// retains a successful result for the session (company config is static per session, matching the
// existing per-component caches), so all callers for the same params share ONE network call. Mirrors
// tinuiti-frontend's `fetchedKeys`. Cleared on failure so a retry is possible. Returns the raw parsed
// JSON; each caller does its own post-processing (and should not mutate the shared object).
const companyInfoCache = new Map<string, Promise<any>>();
export const fetchCompanyInfoRaw = (params: { company: string } & Record<string, any>): Promise<any> => {
  const key = JSON.stringify(params);
  const cached = companyInfoCache.get(key);
  if (cached) {
    return cached;
  }
  const request = UsersLambdaFetch("/company", { params })
    .then(awaitJSON)
    .catch(e => {
      companyInfoCache.delete(key);
      throw e;
    });
  companyInfoCache.set(key, request);
  return request;
};

export const useCompanyInfoFetch: (inputCompany?: string) => void = (inputCompany?: string) => {
  const dispatch = useDispatch();
  const locationCompany = useSelector(currentCompanySelector);
  const company = R.defaultTo(locationCompany, inputCompany);
  const companyInfo = useSelector(companyInfoSelector(company || ""));
  const [companyInfoMap, setCompanyInfoMap] = useState<Record<string, CompanyInfo>>({});
  const setError = useSetError();
  const { location } = useLocation();
  const FLAT_ROUTES = useMemo(() => flattenRoutes(LOCATIONS), []);
  const isTestCompany = TEST_COMPANIES.includes(company);

  // channel/pageID only matter for TEST companies — they drive adjustTestCompanyWrapper's
  // test-company remap on the backend. For normal companies the /company response is identical
  // with or without them, so we omit them to avoid a redundant refetch + cache-key fragmentation.
  const testParams = useMemo(() => {
    if (!isTestCompany) {
      return undefined;
    }
    const page = R.find(
      route => location.pathname.includes((route as { to: string }).to),
      R.values(FLAT_ROUTES)
    );
    if (page) {
      return (page as { to: string; testCompanyParams: Record<string, any> }).testCompanyParams;
    }
  }, [isTestCompany, FLAT_ROUTES, location]);

  const companyInfoKey = useMemo(
    () =>
      `${company}_${testParams ? testParams.channel : ""}_${testParams ? testParams.pageID : ""}`,
    [company, testParams]
  );

  useEffect(() => {
    if (
      companyInfoMap[companyInfoKey] &&
      !R.equals(companyInfo, { ...companyInfoMap[companyInfoKey], cid: company })
    ) {
      dispatch(SetCompanyInfo(company, companyInfoMap[companyInfoKey]));
    }
  }, [company, companyInfo, companyInfoKey, companyInfoMap, dispatch]);

  useEffect(() => {
    if (
      (R.isNil(companyInfo) || R.isNil(companyInfoMap[companyInfoKey])) &&
      company &&
      location &&
      location.pathname &&
      FLAT_ROUTES &&
      !R.isEmpty(FLAT_ROUTES)
    ) {
      (async () => {
        try {
          const rawInfo = await fetchCompanyInfoRaw({
            company,
            ...(testParams || {}),
          });
          // Shallow-clone so the fiscal_cal processing below doesn't mutate the shared cached object.
          const info = rawInfo ? { ...rawInfo } : rawInfo;

          // TODO remove after beta testing OR pick a test cal
          if (info && isTestCompany) {
            info.fiscal_cal = {}; // standard cal
          }

          if (info && info.fiscal_cal) {
            info.fiscal_cal = keysToCamel(info.fiscal_cal);
            if (info.fiscal_cal.calType === FiscalCalType.PATTERN) {
              info.fiscal_cal.generatedPatternCal = genPatternBasedCal(info.fiscal_cal);
            }
          }
          setCompanyInfoMap(current => ({ ...current, [companyInfoKey]: info }));
          dispatch(SetCompanyInfo(company, info));
        } catch (e) {
          const reportError = e as Error;
          setError({
            message: `Failed to fetch info for company "${company}". ${reportError.message}`,
            reportError,
          });
        }
      })();
    }
  }, [
    company,
    companyInfo,
    companyInfoKey,
    companyInfoMap,
    dispatch,
    FLAT_ROUTES,
    isTestCompany,
    location,
    setError,
    testParams,
  ]);
};

const setCompanyInfo = (
  state: CompanyReduxState,
  { company, info }: SetCompanyInfoActionType
): CompanyReduxState => ({
  ...state,
  companies: {
    ...state.companies,
    [company]: {
      ...info,
      cid: company,
    },
  },
});

const setCurrentCompany = (
  state: CompanyReduxState,
  { company }: SetCurrentCompanyActionType
): CompanyReduxState => ({
  ...state,
  current: company,
});

const setFilteredKpis = (
  state: CompanyReduxState,
  { company, filteredKpis }: SetFilteredKpisActionType
): CompanyReduxState => ({
  ...state,
  filteredKpis: {
    ...state.filteredKpis,
    [company]: filteredKpis,
  },
});

const setFiscalCalendar = (
  state: CompanyReduxState,
  { company, fiscalCal }: SetFiscalCalActionType
): CompanyReduxState => ({
  ...state,
  companies: {
    ...state.companies,
    [company]: {
      ...state.companies[company],
      fiscal_cal: fiscalCal,
    } as CompanyInfo,
  },
});

export default makeReducer<CompanyReduxState>(
  {
    current: "",
    companies: {},
    filteredKpis: {},
  },
  {
    [SET_COMPANY_INFO]: setCompanyInfo,
    [SET_CURRENT_COMPANY]: setCurrentCompany,
    [SET_FILTERED_KPIS]: setFilteredKpis,
    [SET_FISCAL_CAL]: setFiscalCalendar,
  }
);
